import { httpRequest, parseQuery } from '@calendly/http-tools'
import { parseInTimezone, isValidTimezone, now, toDateId, startOfMonth } from '@calendly/datetime'

import {
  canProcessRangesInParallel,
  schedulingLinkSelector
} from 'selectors/event_type'
import { eventTypeSelector } from 'root-api'
import { addRumAction } from 'services/rum'
import { isPublisherSelector, timezoneSelector } from 'selectors'
import { isDiagnosticsEnabledSelector } from 'selectors/diagnostics'
import { currentInviteeSelector, isReschedulingSelector } from 'selectors/current_invitee'

const BOOKING_METHODS = {
  instant: 'instant',
  poll: 'poll',
}

const buildReschedulingParams = (state) => ({
  is_publisher: isPublisherSelector(state),
  new_event_user_uuid: parseQuery(window.location.search).new_event_user_uuid,
  invitee_uuid: currentInviteeSelector(state).uuid
})

const buildPollRevoteParams = () => ({
  invitee_uuid: parseQuery(window.location.search).uuid
})

const buildParams = (state, range, duration) => {
  const schedulingLink = schedulingLinkSelector(state)
  const params = {
    timezone: timezoneSelector(state),
    diagnostics: isDiagnosticsEnabledSelector(state),
    range_start: range[0],
    range_end: range[1],
    embed_domain: parseQuery(window.location.search).embed_domain,
    embed_type: parseQuery(window.location.search).embed_type,
    scheduling_link_uuid: schedulingLink?.uid
  }

  if (duration) params.duration_override = duration

  if (eventTypeSelector(state).booking_method === BOOKING_METHODS.poll) {
    params.rescheduling = buildPollRevoteParams(state)
  }

  if (isReschedulingSelector(state)) params.rescheduling = buildReschedulingParams(state)

  return params
}

const fetchCalendarRange = (eventType, params, pathSegment = 'calendar') => (
  httpRequest({
    url: `/api/booking/event_types/${eventType.uuid}/${pathSegment}/range`,
    type: 'GET',
    params
  })
)

// Keyed by everything that affects the response; entries live only while the request is pending.
const inFlightRangeRequests = new Map()

const RANGE_REQUEST_OUTCOMES = { true: 'success', false: 'failure' }

const trackRangeRequest = (state, eventType, params, startedAt, outcome) => {
  addRumAction('booking_calendar_range_request', {
    event_type_uuid: eventType.uuid,
    event_type_kind: eventType.kind,
    pooling_type: eventType.pooling_type,
    multi_publisher: eventType.multi_publisher,
    processing_type: canProcessRangesInParallel(state) ? 'parallel' : 'sequential',
    range_start: params.range_start,
    range_end: params.range_end,
    diagnostics: params.diagnostics,
    duration_ms: Math.round(performance.now() - startedAt),
    outcome
  })
}

export const fetchRangeData = (state, range, duration) => {
  const eventType = eventTypeSelector(state)
  const params = buildParams(state, range, duration)
  const key = JSON.stringify([eventType.uuid, params])

  if (inFlightRangeRequests.has(key)) return inFlightRangeRequests.get(key)

  const startedAt = performance.now()
  const request = fetchCalendarRange(eventType, params)
    .then(
      (result) => {
        trackRangeRequest(state, eventType, params, startedAt, RANGE_REQUEST_OUTCOMES[result[0]])
        return result
      },
      (error) => {
        trackRangeRequest(state, eventType, params, startedAt, 'error')
        throw error
      }
    )
    .finally(() => inFlightRangeRequests.delete(key))

  inFlightRangeRequests.set(key, request)
  return request
}

/** Run async fn for each item sequentially; use when parallel calls are restricted (e.g. Microsoft). */
export const runSequentially = (items, fn) => items.reduce(
  (promise, item) => promise.then((results) => fn(item).then((result) => [...results, result])),
  Promise.resolve([])
)

export const fetchAvailabilityTracings = (state, range, duration) => {
  const params = {
    ...buildParams(state, range, duration),
    diagnostics: true // Always enable diagnostics for tracings
  }
  return fetchCalendarRange(eventTypeSelector(state), params, 'calendar_tracings')
}

export const getMonthGetParam = () => {
  const { month } = parseQuery(window.location.search)
  return month
}

const wrapRangesInStrings = (ranges, monthId, timezone) => {
  const monthDt = parseInTimezone(monthId, timezone)

  return ranges.map((range) => {
    const start = toDateId(monthDt.set({ day: range.start }))
    const end = toDateId(monthDt.set({ day: range.end }))
    return [start, end]
  })
}

const validateInputs = (monthId, timezone) => {
  if (typeof monthId !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(monthId)) throw new Error(`Invalid monthId: ${monthId}`)
  if (!timezone || !isValidTimezone(timezone)) throw new Error(`Invalid timezone: ${timezone}`)

  const monthDt = parseInTimezone(monthId, timezone)
  if (!monthDt?.isValid) throw new Error(`Invalid monthId: ${monthId}`)

  return monthDt
}

// One request covers the visible period for every event type, including round robin
// and collective. Weekly chunks repeated calendar auth per host without shrinking
// the provider query.
export const buildRanges = (monthId, isFullMonth, timezone) => {
  const monthDt = validateInputs(monthId, timezone)
  const currentTime = now(timezone)
  const fullMonth = isFullMonth || toDateId(startOfMonth(currentTime)) !== monthId
  const periodStartDay = fullMonth ? 1 : Math.min(currentTime.day, monthDt.daysInMonth)
  const periodEndDay = monthDt.daysInMonth

  return wrapRangesInStrings([{ start: periodStartDay, end: periodEndDay }], monthId, timezone)
}
